<template>
  <div class="assistant-panel" :class="{ dark: isDark }">
    <div class="assistant-header">
      <v-icon size="small" class="mr-2">mdi-robot-happy-outline</v-icon>
      <span class="text-subtitle-2 font-weight-medium">AI アシスタント</span>
      <v-chip size="x-small" variant="tonal" class="ml-2">{{ tabLabel }}</v-chip>
      <v-spacer />
      <v-btn
        v-if="messages.length"
        size="small"
        variant="text"
        prepend-icon="mdi-broom"
        :disabled="streaming"
        @click="clearConversation"
      >
        クリア
      </v-btn>
      <v-btn icon="mdi-close" size="small" variant="text" @click="$emit('close')" />
    </div>

    <div class="assistant-hint text-caption">
      シナリオ / Personality / ナレッジの相談・編集、会話ログの検索ができます（会話ログは参照のみ）。
    </div>

    <div ref="listEl" class="assistant-messages">
      <div v-if="messages.length === 0" class="assistant-empty text-caption">
        例:「この脚本を桃太郎風に直して」<br />
        「過去に鬼が出てくる会話を探して、開いて」<br />
        「opening_kickoff は何を選べばいい?」
      </div>

      <div v-for="(m, i) in messages" :key="i" class="assistant-bubble" :class="m.role">
        <div class="assistant-bubble-body" v-text="m.content" />
        <div v-if="m.updates && m.updates.length" class="assistant-updates">
          <v-chip
            v-for="(u, j) in m.updates"
            :key="j"
            size="x-small"
            color="success"
            variant="tonal"
            prepend-icon="mdi-check"
          >
            {{ updateLabel(u) }}
          </v-chip>
        </div>
        <v-progress-circular
          v-if="m.role === 'assistant' && m.streaming && !m.content && !(m.updates && m.updates.length)"
          indeterminate
          size="14"
          width="2"
          class="ml-1"
        />
        <div v-if="m.role === 'assistant' && !m.streaming && m.content" class="assistant-feedback">
          <v-btn
            v-if="!m._feedback"
            size="x-small"
            variant="text"
            density="compact"
            @click="reportUnhelpful(i)"
          >👎 役に立たなかった</v-btn>
          <span v-else class="text-caption text-disabled">フィードバックを送信しました</span>
        </div>
      </div>

      <div v-if="toolActivity" class="assistant-tool-activity text-caption">
        <v-progress-circular indeterminate size="12" width="2" class="mr-1" />
        {{ toolActivity }}
      </div>
    </div>

    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      density="compact"
      class="ma-2"
      closable
      @click:close="error = ''"
    >
      {{ error }}
    </v-alert>

    <div class="assistant-input">
      <v-textarea
        v-model="draft"
        placeholder="メッセージを入力（Enter で送信 / Shift+Enter で改行）"
        rows="1"
        max-rows="6"
        auto-grow
        variant="outlined"
        density="compact"
        hide-details
        class="chat-size-input"
        @keydown.enter.exact.prevent="send"
        @keydown.up="onArrowUp"
        @keydown.down="onArrowDown"
      />
      <v-btn
        icon="mdi-send"
        color="primary"
        size="small"
        class="ml-2"
        :loading="streaming"
        :disabled="!draft.trim()"
        @click="send"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from "vue";
import { useRouter } from "vue-router";
import { useTheme } from "vuetify";
import { sendAssistantMessageStream, sendAssistantFeedback } from "@/api/aiNpcAssistantApi";
import { useAiNpcAssistantStore } from "@/stores/aiNpcAssistantStore";

const props = defineProps({
  // { tab: "scenarios"|"personalities"|"conversation_logs"|"knowledge", objectId?: string }
  context: { type: Object, required: true },
});
defineEmits(["close"]);

const router = useRouter();
const theme = useTheme();
const isDark = computed(() => theme.global.current.value.dark);

const store = useAiNpcAssistantStore();
const messages = computed(() => store.messages);

const draft = ref("");
const streaming = ref(false);
const error = ref("");
const toolActivity = ref("");
const listEl = ref(null);

// 上下矢印での入力履歴呼び出し（あなたとのチャットと同じ方式）。
// 履歴は過去に送信した user メッセージ。null = 非ナビゲーション中。
const historyIndex = ref(null);
function sentHistory() {
  return store.messages.filter((m) => m.role === "user").map((m) => m.content);
}
function onArrowUp(e) {
  const hist = sentHistory();
  // 呼び出した文言を編集していたらナビゲーションを抜ける（通常のカーソル移動へ）。
  if (historyIndex.value !== null && draft.value !== hist[historyIndex.value]) {
    historyIndex.value = null;
  }
  if (historyIndex.value === null) {
    if (draft.value !== "") return; // 空でないときは通常のカーソル移動に任せる
    if (!hist.length) return;
    historyIndex.value = hist.length - 1; // 最新から
  } else if (historyIndex.value > 0) {
    historyIndex.value -= 1; // より古いものへ
  } else {
    e.preventDefault(); // 既に最古
    return;
  }
  draft.value = hist[historyIndex.value];
  e.preventDefault();
}
function onArrowDown(e) {
  const hist = sentHistory();
  if (historyIndex.value === null) return; // 非ナビゲーション中は通常のカーソル移動
  if (draft.value !== hist[historyIndex.value]) {
    historyIndex.value = null; // 編集済みなら抜ける
    return;
  }
  if (historyIndex.value < hist.length - 1) {
    historyIndex.value += 1; // より新しいものへ
    draft.value = hist[historyIndex.value];
    e.preventDefault();
  } else {
    historyIndex.value = null; // 最新より先は空欄に戻す
    draft.value = "";
    e.preventDefault();
  }
}

const TAB_LABELS = {
  scenarios: "シナリオ",
  personalities: "Personality",
  conversation_logs: "会話ログ",
  knowledge: "ナレッジ",
};
const tabLabel = computed(() => TAB_LABELS[props.context?.tab] || "AiNpc");

const TOOL_LABELS = {
  update_scenario: "シナリオを更新中…",
  create_scenario: "シナリオを作成中…",
  update_personality: "Personality を更新中…",
  create_personality: "Personality を作成中…",
  update_knowledge: "ナレッジを更新中…",
  create_knowledge: "ナレッジを作成中…",
  search_conversation_logs: "会話ログを検索中…",
  get_conversation_log: "会話ログを読み込み中…",
  list_scenarios: "一覧を参照中…",
  list_personalities: "一覧を参照中…",
  list_knowledge: "一覧を参照中…",
  get_scenario: "対象を参照中…",
  get_personality: "対象を参照中…",
  get_knowledge: "対象を参照中…",
  open_object: "対象を開いています…",
};

const OPEN_ROUTES = {
  scenarios: "AiNpcScenarioEdit",
  personalities: "AiNpcPersonalityEdit",
  conversation_logs: "AiNpcConversationLogDetail",
  knowledge: "AiNpcKnowledgeEdit",
};

function updateLabel(u) {
  const tab = TAB_LABELS[u.tab] || u.tab;
  if (u.created) return `${tab}を作成`;
  const f = (u.changed_fields || []).join(", ");
  return `${tab}更新${f ? ": " + f : ""}`;
}

function clearConversation() {
  store.clear();
  error.value = "";
}

async function reportUnhelpful(index) {
  const upto = store.messages
    .slice(0, index + 1)
    .filter((m) => !(m.role === "assistant" && m.streaming))
    .map((m) => ({ role: m.role, content: m.content }));
  const ctx = { tab: props.context?.tab, object_id: props.context?.objectId };
  try {
    await sendAssistantFeedback(ctx, upto);
    if (store.messages[index]) store.messages[index]._feedback = true;
  } catch (_) {
    /* フィードバック失敗は致命的でない */
  }
}

async function scrollToBottom() {
  await nextTick();
  if (listEl.value) listEl.value.scrollTop = listEl.value.scrollHeight;
}

async function send() {
  const text = draft.value.trim();
  if (!text || streaming.value) return;
  error.value = "";

  store.messages.push({ role: "user", content: text });
  draft.value = "";
  historyIndex.value = null;

  const assistantMsg = { role: "assistant", content: "", streaming: true, updates: [] };
  store.messages.push(assistantMsg);
  streaming.value = true;
  await scrollToBottom();

  const history = store.messages
    .filter((m) => !(m.role === "assistant" && m.streaming))
    .map((m) => ({ role: m.role, content: m.content }));

  const ctx = { tab: props.context?.tab, object_id: props.context?.objectId };

  await sendAssistantMessageStream(ctx, history, {
    onToken: (t) => {
      assistantMsg.content += t;
      scrollToBottom();
    },
    onToolCall: (name) => {
      toolActivity.value = TOOL_LABELS[name] || `${name} を実行中…`;
      scrollToBottom();
    },
    onObjectUpdated: (d) => {
      assistantMsg.updates.push(d);
      store.signalUpdate(d); // 編集ビューが自分の対象なら再読込する
    },
    onOpenObject: (d) => {
      const name = OPEN_ROUTES[d.tab];
      if (name && d.id) router.push({ name, params: { id: d.id } }).catch(() => {});
    },
    onDone: () => {
      assistantMsg.streaming = false;
      streaming.value = false;
      toolActivity.value = "";
      scrollToBottom();
    },
    onError: (e) => {
      assistantMsg.streaming = false;
      streaming.value = false;
      toolActivity.value = "";
      if (!assistantMsg.content && !assistantMsg.updates.length) {
        const idx = store.messages.indexOf(assistantMsg);
        if (idx !== -1) store.messages.splice(idx, 1);
      }
      error.value = e || "エラーが発生しました";
    },
  });
}
</script>

<style scoped>
.assistant-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fafafa;
}
.assistant-panel.dark {
  background: #1e1e1e;
}
.assistant-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.assistant-panel.dark .assistant-header {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}
.assistant-hint {
  padding: 6px 12px;
  color: #888;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.assistant-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.assistant-empty {
  color: #999;
  line-height: 1.8;
  margin: auto 0;
}
.assistant-updates {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.assistant-tool-activity {
  align-self: flex-start;
  color: #888;
  padding: 2px 4px;
}
.assistant-bubble {
  max-width: 92%;
  padding: 8px 12px;
  border-radius: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.875rem;
  line-height: 1.5;
}
.assistant-bubble.user {
  align-self: flex-end;
  background: #1976d2;
  color: #fff;
  border-bottom-right-radius: 4px;
}
.assistant-bubble.assistant {
  align-self: flex-start;
  background: #fff;
  color: #222;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-bottom-left-radius: 4px;
}
.assistant-panel.dark .assistant-bubble.assistant {
  background: #2a2a2a;
  color: #eee;
  border-color: rgba(255, 255, 255, 0.1);
}
.assistant-feedback {
  margin-top: 2px;
  opacity: 0.7;
}
.assistant-input {
  display: flex;
  align-items: flex-end;
  padding: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.assistant-panel.dark .assistant-input {
  border-top-color: rgba(255, 255, 255, 0.1);
}
/* 入力欄のフォントを小さめ（0.75rem = 12px）に。
   Vuetify 既定（.v-field__input 16px）が特異度で勝つため !important で確実に上書き。 */
.chat-size-input :deep(textarea),
.chat-size-input :deep(.v-field__input),
.chat-size-input :deep(input) {
  font-size: 0.75rem !important;
  line-height: 1.5 !important;
}
.chat-size-input :deep(textarea::placeholder) {
  font-size: 0.75rem !important;
}
</style>
